import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, AlertCircle, Search, CheckCircle2, XCircle, PlayCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ReenrichPreviewButton } from "@/components/diagnose/ReenrichPreviewButton";

/**
 * Admin-only "why is my date filter showing zero?" diagnostic page.
 * Hits GET /api/admin/diagnose/ops-dates and pretty-prints the result.
 */
export default function DiagnosePage() {
  const { user } = useAuth();
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Diagnose a user" state
  const [userQuery, setUserQuery] = useState("");
  const [userBrand, setUserBrand] = useState("");
  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);

  // "Fix attempt_date_ist" one-time corrective backfill state
  const [fixLoading, setFixLoading] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const [fixError, setFixError] = useState(null);

  // "Purge by date range" state — nuclear cleanup for stale dashboard data
  const [purgeFrom, setPurgeFrom] = useState("");
  const [purgeTo, setPurgeTo] = useState("");
  const [purgeBrand, setPurgeBrand] = useState("");
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeResult, setPurgeResult] = useState(null);
  const [purgeError, setPurgeError] = useState(null);

  // "Audit dashboard vs uploads" — the cross-check that surfaces "ghost
  // dates" (rows visible on the dashboard whose parent upload/report was
  // already deleted).
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditError, setAuditError] = useState(null);
  const [orphanBusy, setOrphanBusy] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);

  // Retag SMS-eligible dispositions
  const [retagLoading, setRetagLoading] = useState(false);
  const [retagResult, setRetagResult] = useState(null);
  const [retagError, setRetagError] = useState(null);

  // Backfill credit_date_ist + switch to deposit-date bucketing
  const [bfLoading, setBfLoading] = useState(false);
  const [bfResult, setBfResult] = useState(null);
  const [bfError, setBfError] = useState(null);

  // "Converted users in month" diagnostic — one row per (user, brand)
  // credited in the picked month, with masked phone + on-day/after
  // amounts. Lets ops spot-check a handful against MySQL by hand.
  const _prevMonth = (() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [cxMonth, setCxMonth] = useState(_prevMonth);
  const [cxTeam, setCxTeam] = useState("");
  const [cxBrand, setCxBrand] = useState("");
  const [cxSearch, setCxSearch] = useState("");
  const [cxLimit, setCxLimit] = useState(200);
  const [cxLoading, setCxLoading] = useState(false);
  const [cxData, setCxData] = useState(null);
  const [cxError, setCxError] = useState(null);
  const [cxTeams, setCxTeams] = useState([]);
  const [cxSort, setCxSort] = useState({ key: "amount_after", dir: "desc" });

  useEffect(() => {
    api.get("/teams").then((r) => setCxTeams(r.data || [])).catch(() => {});
  }, []);

  const runConvertedUsers = async () => {
    setCxLoading(true);
    setCxError(null);
    setCxData(null);
    try {
      const params = new URLSearchParams();
      params.set("month", cxMonth);
      if (cxTeam) params.set("team_id", cxTeam);
      if (cxBrand.trim()) params.set("brand_id", cxBrand.trim());
      if (cxSearch.trim()) params.set("search", cxSearch.trim());
      params.set("limit", String(cxLimit));
      const { data } = await api.get(`/admin/diagnose/converted-users-in-month?${params.toString()}`);
      setCxData(data);
    } catch (e) {
      setCxError(e?.response?.data?.detail || e.message);
    } finally {
      setCxLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!cxData?.users?.length) return;
    const cols = [
      "customer_name", "phone_number_masked", "brand_id", "team_id",
      "attempts_in_month", "attempt_date_ist", "credit_date_ist", "agent_name",
      "campaign_name", "amount_on_day", "ftd_on_day", "amount_after",
      "first_deposit_after_date", "last_deposit_date_after",
      "txns_on_day", "txns_after", "ops_user_id",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = [cols.join(",")];
    cxData.users.forEach((r) => {
      rows.push(cols.map((c) => escape(r[c])).join(","));
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `converted_users_${cxData.month}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
  };

  // Per-user Mongo ↔ MySQL cross-check (opened in a modal-style panel).
  const [xCheck, setXCheck] = useState(null);     // {customer_name, brand_id} or null
  const [xData, setXData] = useState(null);
  const [xLoading, setXLoading] = useState(false);
  const [xError, setXError] = useState(null);

  const openCrossCheck = async (row) => {
    setXCheck({ customer_name: row.customer_name, brand_id: row.brand_id });
    setXData(null);
    setXError(null);
    setXLoading(true);
    try {
      const params = new URLSearchParams({
        customer_name: row.customer_name,
        brand_id: String(row.brand_id),
        month: cxMonth,
      });
      const { data } = await api.get(`/admin/diagnose/user-cross-check?${params.toString()}`);
      setXData(data);
    } catch (e) {
      setXError(e?.response?.data?.detail || e.message);
    } finally {
      setXLoading(false);
    }
  };

  // RD dedup drift repair — one-off backfill for the historical bug
  // where RD credits got stolen from previous months on repeat converts.
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  const [repairError, setRepairError] = useState(null);
  const [repairStarted, setRepairStarted] = useState(null);

  const runRepair = async () => {
    if (!window.confirm(
      "This restores every ops_call_row that was demoted by the old " +
      "dedup logic, re-runs dedup with the credit-date-aware key, " +
      "rebuilds main_daily_agg, and wipes the analytics cache. " +
      "Runs across ALL history. Takes 5-15 minutes.\n\n" +
      "It is safe to run live (read-heavy, no locks). Continue?"
    )) return;
    setRepairLoading(true);
    setRepairError(null);
    setRepairResult(null);
    setRepairStarted(Date.now());
    try {
      const { data } = await api.post("/admin/repair-rd-dedup-drift");
      setRepairResult(data);
    } catch (e) {
      setRepairError(e?.response?.data?.detail || e.message);
    } finally {
      setRepairLoading(false);
    }
  };

  // Deep re-enrichment from MySQL (queries payment DB per legacy row)
  const [reBrand, setReBrand] = useState("");
  const [reFrom, setReFrom] = useState("");
  const [reTo, setReTo] = useState("");
  const [reLoading, setReLoading] = useState(false);
  const [reResult, setReResult] = useState(null);
  const [reError, setReError] = useState(null);
  // Continuous re-enrichment status (polled)
  const [reStatus, setReStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/admin/reenrichment-status");
        if (!cancelled) setReStatus(data);
      } catch (_) { /* ignore */ }
    };
    poll();
    const iv = setInterval(poll, 5000);   // 5s while page open
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const runReenrich = async () => {
    const allBrands = !reBrand.trim();
    if (!window.confirm(
      "This will re-query MySQL for every ops_call_row in the chosen " +
      (allBrands ? "date range across ALL BRANDS" : "brand + date range") +
      " to repopulate deposit-bucket fields (including first_deposit_after_date) " +
      "and rebucket credits by TRUE deposit date. Takes " +
      (allBrands ? "10-30 min" : "5-15 min") + ". Proceed?"
    )) return;
    setReLoading(true);
    setReError(null);
    setReResult(null);
    try {
      const { data } = await api.post("/admin/reenrich-from-mysql", {
        brand_id: reBrand.trim() ? Number(reBrand) : null,
        date_from: reFrom, date_to: reTo,
      });
      setReResult(data);
    } catch (e) {
      setReError(e?.response?.data?.detail || e.message);
    } finally {
      setReLoading(false);
    }
  };

  // Diagnose ONE customer's credit-attribution (raw row + rollup docs +
  // per-day histogram). Answers "why is X showing as converted on Y?".
  const [dxBrand, setDxBrand] = useState("");
  const [dxUser, setDxUser] = useState("");
  const [dxFrom, setDxFrom] = useState("");
  const [dxTo, setDxTo] = useState("");
  const [dxLoading, setDxLoading] = useState(false);
  const [dxData, setDxData] = useState(null);
  const [dxError, setDxError] = useState(null);

  const runDiagnoseUser = async () => {
    setDxLoading(true);
    setDxError(null);
    setDxData(null);
    try {
      const params = new URLSearchParams();
      params.set("brand_id", (dxBrand || "").trim());
      params.set("username", (dxUser || "").trim());
      if (dxFrom) params.set("date_from", dxFrom);
      if (dxTo) params.set("date_to", dxTo);
      const { data } = await api.get(`/admin/diagnose-user-credit?${params.toString()}`);
      setDxData(data);
    } catch (e) {
      setDxError(e?.response?.data?.detail || e.message);
    } finally {
      setDxLoading(false);
    }
  };

  const runBackfillCreditDate = async () => {
    if (!window.confirm(
      "Recompute credit for EVERY ops_call_row using current rules " +
      "(SMS-eligible dispositions, cooldown, deposit window), populate " +
      "credit_date_ist, re-run dedup, and rebuild BOTH rollups so " +
      "dashboards bucket by DEPOSIT DATE.\n\n" +
      "This is the full nuclear reclassification. Runs on all history — " +
      "may take a few minutes on large datasets. Safe to re-run."
    )) return;
    setBfLoading(true);
    setBfError(null);
    setBfResult(null);
    try {
      const { data } = await api.post("/admin/backfill-credit-date-ist");
      setBfResult(data);
    } catch (e) {
      setBfError(e?.response?.data?.detail || e.message);
    } finally {
      setBfLoading(false);
    }
  };

  // Credit reasons breakdown
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasonsData, setReasonsData] = useState(null);
  const [reasonsError, setReasonsError] = useState(null);
  const [reasonsFrom, setReasonsFrom] = useState("");
  const [reasonsTo, setReasonsTo] = useState("");
  const [reasonsBrand, setReasonsBrand] = useState("");

  const runReasons = async () => {
    setReasonsLoading(true);
    setReasonsError(null);
    setReasonsData(null);
    try {
      const params = new URLSearchParams();
      if (reasonsFrom) params.set("date_from", reasonsFrom);
      if (reasonsTo) params.set("date_to", reasonsTo);
      if (reasonsBrand.trim()) params.set("brand_id", reasonsBrand.trim());
      const { data } = await api.get(`/admin/credit-reasons-summary?${params.toString()}`);
      setReasonsData(data);
    } catch (e) {
      setReasonsError(e?.response?.data?.detail || e.message);
    } finally {
      setReasonsLoading(false);
    }
  };

  const runRetagSms = async () => {
    if (!window.confirm(
      "Reclassify historical no-credit rows whose disposition looks like " +
      "'no answer' / 'unreachable' / 'busy' / 'voicemail' / 'language barrier' " +
      "and grant them SMS credit (subject to campaign cooldown + window rules)?\n\n" +
      "This scans ALL ops_call_rows, retags matching rows, re-runs the " +
      "deduplication attribution, and rebuilds daily rollups. Safe to re-run."
    )) return;
    setRetagLoading(true);
    setRetagError(null);
    setRetagResult(null);
    try {
      const { data } = await api.post("/admin/retag-sms-eligible");
      setRetagResult(data);
    } catch (e) {
      setRetagError(e?.response?.data?.detail || e.message);
    } finally {
      setRetagLoading(false);
    }
  };

  const runAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    setAuditData(null);
    setOrphanResult(null);
    try {
      const { data } = await api.get("/admin/audit-dashboard-vs-uploads");
      setAuditData(data);
    } catch (e) {
      setAuditError(e?.response?.data?.detail || e.message);
    } finally {
      setAuditLoading(false);
    }
  };

  const purgeOrphans = async () => {
    if (!auditData) return;
    const s = auditData.summary;
    const total = (s.orphan_rows_total || 0) + (s.null_parent_rows_total || 0);
    if (!window.confirm(
      `Delete ${total.toLocaleString()} orphan/null-parent rows?\n\n` +
      `• ops_call_rows without a live ops_uploads parent\n` +
      `• ops_call_rows with null upload_id\n` +
      `• report_rows without a live reports parent\n` +
      `• report_rows with null report_id\n\n` +
      `The pre-aggregated rollup will be rebuilt after.\nThis cannot be undone.`
    )) return;
    setOrphanBusy(true);
    try {
      const { data } = await api.post("/admin/purge-orphans");
      setOrphanResult(data);
      // Re-run audit so the UI reflects the clean state
      await runAudit();
    } catch (e) {
      setAuditError(e?.response?.data?.detail || e.message);
    } finally {
      setOrphanBusy(false);
    }
  };

  // Nuclear wipe: delete ALL analytics/upload data so you can start fresh.
  // Two-step: user types "WIPE ALL" in a prompt to prevent accidents.
  const [wipeBusy, setWipeBusy] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  const [wipeError, setWipeError] = useState(null);
  const wipeAll = async () => {
    const phrase = window.prompt(
      "☢ NUCLEAR WIPE\n\n" +
      "This will DELETE every ops_call_row, upload record, report_row, report, " +
      "and pre-aggregated rollup doc. Users, teams, brands, and campaigns are preserved.\n\n" +
      "Type WIPE ALL (all caps) to confirm.",
    );
    if ((phrase || "").trim() !== "WIPE ALL") {
      setWipeError("Confirmation phrase incorrect — nothing was deleted.");
      return;
    }
    setWipeBusy(true);
    setWipeError(null);
    setWipeResult(null);
    try {
      const { data } = await api.post("/admin/wipe-all-analytics-data", { confirm: "WIPE ALL" });
      setWipeResult(data);
      setAuditData(null);   // audit results are now meaningless
      setOrphanResult(null);
    } catch (e) {
      setWipeError(e?.response?.data?.detail || e.message);
    } finally {
      setWipeBusy(false);
    }
  };

  // On-demand snapshot backup — writes a gzipped mongodump to disk.
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupResult, setBackupResult] = useState(null);
  const [backupError, setBackupError] = useState(null);
  const runBackup = async () => {
    if (!window.confirm(
      "Take a mongodump snapshot NOW?\n\n" +
      "Writes a gzipped archive to /app/backups on the backend container.\n" +
      "Recommended before any risky operation (wipe, purge, mass edit)."
    )) return;
    setBackupBusy(true);
    setBackupError(null);
    setBackupResult(null);
    try {
      const { data } = await api.post("/admin/backup-now");
      setBackupResult(data);
    } catch (e) {
      setBackupError(e?.response?.data?.detail || e.message);
    } finally {
      setBackupBusy(false);
    }
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/diagnose/ops-dates");
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const runUser = async () => {
    // Normalize: allow newlines / semicolons / spaces as separators too.
    const names = userQuery
      .split(/[,;\n\r]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) return;
    setUserLoading(true);
    setUserError(null);
    setUserData(null);
    try {
      const params = { customer_name: names.join(",") };
      if (userBrand.trim()) params.brand_id = parseInt(userBrand, 10);
      const { data } = await api.get("/admin/diagnose/user", { params });
      setUserData(data);
    } catch (e) {
      setUserError(e?.response?.data?.detail || e.message);
    } finally {
      setUserLoading(false);
    }
  };

  const runFix = async () => {
    if (!window.confirm(
      "Re-stamp attempt_date_ist on EVERY ops_call_row using the corrected DD/MM/YYYY parser?\n\n" +
      "This is safe (idempotent) but scans all rows and may take a couple minutes on large datasets."
    )) return;
    setFixLoading(true);
    setFixError(null);
    setFixResult(null);
    try {
      const { data } = await api.post("/admin/fix-attempt-date-ist");
      setFixResult(data);
    } catch (e) {
      setFixError(e?.response?.data?.detail || e.message);
    } finally {
      setFixLoading(false);
    }
  };

  const runPurgePreview = async () => {
    if (!purgeFrom || !purgeTo) return;
    setPurgeLoading(true);
    setPurgeError(null);
    setPurgeResult(null);
    setPurgePreview(null);
    try {
      const body = { date_from: purgeFrom, date_to: purgeTo, dry_run: true };
      if (purgeBrand.trim()) body.brand_id = parseInt(purgeBrand, 10);
      const { data } = await api.post("/admin/purge-by-date", body);
      setPurgePreview(data);
    } catch (e) {
      setPurgeError(e?.response?.data?.detail || e.message);
    } finally {
      setPurgeLoading(false);
    }
  };

  const runPurgeConfirm = async () => {
    if (!purgePreview) return;
    const w = purgePreview.would_delete;
    const total = (w?.ops_call_rows || 0) + (w?.report_rows || 0);
    if (!window.confirm(
      `PERMANENTLY delete ${total.toLocaleString()} rows between ${purgeFrom} and ${purgeTo}?\n\n` +
      `• ops_call_rows: ${(w?.ops_call_rows || 0).toLocaleString()}\n` +
      `• report_rows: ${(w?.report_rows || 0).toLocaleString()}\n` +
      `• Uploads touched: ${(w?.ops_upload_ids_touched?.length || 0)}\n` +
      `• Reports touched: ${(w?.report_ids_touched?.length || 0)}\n\n` +
      `Uploads/reports whose entire row-set is wiped will also be deleted.\nThis cannot be undone.`
    )) return;
    setPurgeLoading(true);
    setPurgeError(null);
    try {
      const body = { date_from: purgeFrom, date_to: purgeTo, dry_run: false };
      if (purgeBrand.trim()) body.brand_id = parseInt(purgeBrand, 10);
      const { data } = await api.post("/admin/purge-by-date", body);
      setPurgeResult(data);
      setPurgePreview(null);
    } catch (e) {
      setPurgeError(e?.response?.data?.detail || e.message);
    } finally {
      setPurgeLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="diagnose-page">

      {/* ---- Converted users in a month (physical spot-check tool) ---- */}
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Converted users in month</h1>
          <div className="text-sm text-slate-600 mt-1">
            One row per (user, brand) credited=<span className="font-mono">yes</span> in the picked month. Sort by amount_after
            to spot the biggest deposits and cross-check them physically in MySQL / customer panel.
          </div>
        </div>
        <div className="glass-card p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Month</label>
              <input
                type="month"
                value={cxMonth}
                onChange={(e) => setCxMonth(e.target.value)}
                className="glass-input h-9 w-full mt-1"
                data-testid="cx-month"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Team (optional)</label>
              <select
                value={cxTeam}
                onChange={(e) => setCxTeam(e.target.value)}
                className="glass-input h-9 w-full mt-1"
                data-testid="cx-team"
              >
                <option value="">All teams</option>
                {cxTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Brand ID</label>
              <input
                type="number"
                value={cxBrand}
                onChange={(e) => setCxBrand(e.target.value)}
                placeholder="e.g. 31"
                className="glass-input h-9 w-full mt-1"
                data-testid="cx-brand"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500 uppercase tracking-wide">Search customer name</label>
              <input
                type="text"
                value={cxSearch}
                onChange={(e) => setCxSearch(e.target.value)}
                placeholder="substring, case-insensitive"
                className="glass-input h-9 w-full mt-1"
                data-testid="cx-search"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Limit</label>
              <select
                value={cxLimit}
                onChange={(e) => setCxLimit(Number(e.target.value))}
                className="glass-input h-9 mt-1"
                data-testid="cx-limit"
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1,000</option>
                <option value={5000}>5,000</option>
              </select>
            </div>
            <Button
              onClick={runConvertedUsers}
              disabled={cxLoading || !cxMonth}
              className="bg-[#0A84FF] hover:bg-[#0069cc] mt-4"
              data-testid="cx-run-btn"
            >
              {cxLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</> :
                <><Search className="w-4 h-4 mr-2" /> Load converted users</>}
            </Button>
            {cxData?.users?.length > 0 && (
              <Button
                onClick={downloadCsv}
                variant="outline"
                className="mt-4"
                data-testid="cx-csv-btn"
              >
                Download CSV
              </Button>
            )}
          </div>
        </div>

        {cxError && (
          <div className="glass-card p-3 border-l-4 border-red-500 flex gap-2 items-start"
               data-testid="cx-error">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">{cxError}</div>
          </div>
        )}

        {cxData && (
          <div className="space-y-3" data-testid="cx-results">
            {/* Header stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="glass-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Unique users</div>
                <div className="text-xl font-semibold text-slate-900">
                  {cxData.total_converted_users?.toLocaleString() || 0}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {cxData.total_user_brand_rows?.toLocaleString() || 0} (user, brand) rows
                </div>
              </div>
              <div className="glass-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Amount on-day</div>
                <div className="text-xl font-semibold text-slate-900">
                  {(cxData.grand_totals?.on_day || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="glass-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Amount after (lifetime)</div>
                <div className="text-xl font-semibold text-slate-900">
                  {(cxData.grand_totals?.after || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="glass-card p-3">
                <div className="text-[10px] uppercase tracking-wide text-slate-500">FTD on-day</div>
                <div className="text-xl font-semibold text-slate-900">
                  {(cxData.grand_totals?.ftd_on_day || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>

            {/* User table */}
            <div className="glass-card p-0 overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center justify-between">
                <span>Showing {cxData.returned?.toLocaleString() || 0} of {cxData.total_user_brand_rows?.toLocaleString() || 0} rows</span>
                <span className="text-slate-400">Click a column header to sort</span>
              </div>
              <div className="overflow-auto max-h-[70vh]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                    <tr className="text-slate-500 text-left">
                      {[
                        ["customer_name", "Customer"],
                        ["phone_number_masked", "Phone"],
                        ["brand_id", "Brand"],
                        ["attempt_date_ist", "Call date"],
                        ["credit_date_ist", "Credit date"],
                        ["agent_name", "Agent"],
                        ["campaign_name", "Campaign"],
                        ["amount_on_day", "On-day ₹"],
                        ["ftd_on_day", "FTD on-day"],
                        ["amount_after", "After (lifetime)"],
                        ["txns_after", "Txns after"],
                        ["first_deposit_after_date", "First-after date"],
                        ["last_deposit_date_after", "Last-after date"],
                        ["attempts_in_month", "Attempts"],
                      ].map(([k, label]) => (
                        <th key={k}
                            onClick={() => setCxSort((s) => ({
                              key: k,
                              dir: s.key === k && s.dir === "desc" ? "asc" : "desc",
                            }))}
                            className="px-2 py-2 cursor-pointer whitespace-nowrap hover:text-slate-900 select-none"
                            data-testid={`cx-th-${k}`}>
                          {label}
                          {cxSort.key === k && (
                            <span className="ml-1 text-slate-400">{cxSort.dir === "desc" ? "↓" : "↑"}</span>
                          )}
                        </th>
                      ))}
                      <th className="px-2 py-2">Verify</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...cxData.users].sort((a, b) => {
                      const av = a[cxSort.key];
                      const bv = b[cxSort.key];
                      const na = typeof av === "number" ? av : parseFloat(av);
                      const nb = typeof bv === "number" ? bv : parseFloat(bv);
                      if (!isNaN(na) && !isNaN(nb)) return cxSort.dir === "desc" ? nb - na : na - nb;
                      const sa = String(av ?? ""), sb = String(bv ?? "");
                      return cxSort.dir === "desc" ? sb.localeCompare(sa) : sa.localeCompare(sb);
                    }).map((u) => (
                      <tr key={`${u.ops_user_id}-${u.brand_id}`}
                          className="border-t border-slate-100 hover:bg-slate-50"
                          data-testid={`cx-row-${u.ops_user_id}`}>
                        <td className="px-2 py-1.5 font-medium text-slate-900">{u.customer_name}</td>
                        <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{u.phone_number_masked || "—"}</td>
                        <td className="px-2 py-1.5">{u.brand_id}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{u.attempt_date_ist}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{u.credit_date_ist || "—"}</td>
                        <td className="px-2 py-1.5">{u.agent_name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{u.campaign_name}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{Math.round(u.amount_on_day || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{Math.round(u.ftd_on_day || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-900">{Math.round(u.amount_after || 0).toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right text-slate-500">{u.txns_after || 0}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{u.first_deposit_after_date || "—"}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">{u.last_deposit_date_after || "—"}</td>
                        <td className="px-2 py-1.5 text-center text-slate-500">{u.attempts_in_month}</td>
                        <td className="px-2 py-1.5 text-center">
                          <button
                            type="button"
                            onClick={() => openCrossCheck(u)}
                            className="text-[11px] text-[#0A84FF] hover:underline"
                            data-testid={`cx-verify-${u.customer_name}`}
                          >
                            Verify
                          </button>
                        </td>
                      </tr>
                    ))}
                    {cxData.users.length === 0 && (
                      <tr><td colSpan={15} className="px-2 py-6 text-center text-sm text-slate-500">
                        No converted users match this filter.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Mongo ↔ MySQL cross-check panel */}
        {xCheck && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto"
               onClick={() => setXCheck(null)}
               data-testid="xcheck-overlay">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mt-8 p-5 space-y-4"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="xcheck-panel">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-outfit font-semibold text-slate-900 text-lg">
                    Cross-check · {xCheck.customer_name} · brand {xCheck.brand_id} · {cxMonth}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Left = every raw ops_call_row we stored. Right = every PROCESSED DEPOSIT from MySQL for the same (user, brand, month).
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setXCheck(null)}
                  className="text-slate-500 hover:text-slate-900 text-xl leading-none"
                  data-testid="xcheck-close"
                >×</button>
              </div>

              {xLoading && (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading Mongo + MySQL…
                </div>
              )}
              {xError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{xError}</div>
              )}

              {xData && (
                <>
                  {/* Verdict banner */}
                  <div className={`rounded p-3 border ${xData.verdict?.startsWith("MISMATCH")
                        ? "bg-red-50 border-red-300 text-red-800"
                        : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}
                       data-testid="xcheck-verdict">
                    <div className="font-semibold text-sm">{xData.verdict}</div>
                    {xData.mongo_summary?.shared_customer_names?.length > 0 && (
                      <div className="text-xs mt-1">
                        ⚠ ops_user_id <span className="font-mono">{(xData.mongo_summary.distinct_ops_user_ids || []).join(", ")}</span> is
                        SHARED with {xData.mongo_summary.shared_customer_names.length} other customer(s):
                        <span className="font-mono ml-1">{xData.mongo_summary.shared_customer_names.slice(0, 5).join(", ")}
                        {xData.mongo_summary.shared_customer_names.length > 5 ? " …" : ""}</span>
                      </div>
                    )}
                  </div>

                  {/* Numeric summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div className="bg-slate-50 rounded p-2">
                      <div className="text-[10px] uppercase text-slate-500">Mongo max(after)</div>
                      <div className="font-semibold text-slate-900">
                        {Math.round(xData.mongo_summary?.amount_after_max || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded p-2">
                      <div className="text-[10px] uppercase text-slate-500">MySQL total this month</div>
                      <div className="font-semibold text-slate-900">
                        {Math.round(xData.mysql_summary?.total_amount || 0).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded p-2">
                      <div className="text-[10px] uppercase text-slate-500">Mongo rows</div>
                      <div className="font-semibold text-slate-900">{xData.mongo_row_count}</div>
                    </div>
                    <div className="bg-slate-50 rounded p-2">
                      <div className="text-[10px] uppercase text-slate-500">MySQL deposits</div>
                      <div className="font-semibold text-slate-900">{xData.mysql_row_count}</div>
                    </div>
                  </div>

                  {xData.mysql_error && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                      MySQL query failed: <span className="font-mono">{xData.mysql_error}</span>
                    </div>
                  )}

                  {/* Side-by-side tables */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Mongo ops_call_rows ({xData.mongo_row_count})</div>
                      <div className="max-h-96 overflow-auto border border-slate-200 rounded">
                        <table className="w-full text-[11px]">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr className="text-slate-500 text-left">
                              <th className="px-2 py-1">Call date</th>
                              <th className="px-2 py-1">Credit</th>
                              <th className="px-2 py-1 text-right">on_day</th>
                              <th className="px-2 py-1 text-right">after</th>
                              <th className="px-2 py-1">first-after date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(xData.mongo_rows || []).map((r, i) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="px-2 py-1 whitespace-nowrap">{r.attempt_date_ist}</td>
                                <td className="px-2 py-1">
                                  <span className={r.credit_status === "yes" ? "text-emerald-700" : "text-slate-500"}>
                                    {r.credit_status}
                                  </span>
                                </td>
                                <td className="px-2 py-1 text-right font-mono">{Math.round(Number(r.deposit_on_day_amount) || 0).toLocaleString()}</td>
                                <td className="px-2 py-1 text-right font-mono">{Math.round(Number(r.deposit_sum_after) || 0).toLocaleString()}</td>
                                <td className="px-2 py-1 whitespace-nowrap text-slate-500">{r.first_deposit_after_date || "—"}</td>
                              </tr>
                            ))}
                            {(xData.mongo_rows || []).length === 0 && (
                              <tr><td colSpan={5} className="px-2 py-4 text-center text-slate-500">No Mongo rows.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">MySQL payment.payment_txns ({xData.mysql_row_count})</div>
                      <div className="max-h-96 overflow-auto border border-slate-200 rounded">
                        <table className="w-full text-[11px]">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr className="text-slate-500 text-left">
                              <th className="px-2 py-1">Created (IST)</th>
                              <th className="px-2 py-1">Status</th>
                              <th className="px-2 py-1 text-right">Amount</th>
                              <th className="px-2 py-1">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(xData.mysql_rows || []).map((r) => (
                              <tr key={r.id} className="border-t border-slate-100">
                                <td className="px-2 py-1 whitespace-nowrap font-mono text-[10px]">{r.created}</td>
                                <td className="px-2 py-1">{r.status}</td>
                                <td className="px-2 py-1 text-right font-mono">{Math.round(r.amount || 0).toLocaleString()}</td>
                                <td className="px-2 py-1">{r.payment_type}</td>
                              </tr>
                            ))}
                            {(xData.mysql_rows || []).length === 0 && (
                              <tr><td colSpan={4} className="px-2 py-4 text-center text-slate-500">No MySQL deposits.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---- RD dedup drift repair (one-off backfill) ---- */}
      <div className="space-y-3" data-testid="repair-rd-section">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-amber-500" />
            Repair RD dedup drift
          </h1>
          <div className="text-sm text-slate-600 mt-1 max-w-3xl">
            One-off backfill. Restores ops_call_rows that were demoted by the old
            <span className="font-mono mx-1 text-xs bg-slate-100 px-1 py-0.5 rounded">(brand, customer)</span>
            dedup which incorrectly stole RD credits from previous months whenever the same customer converted again later. After running,
            historical July / June RD counts will jump back up to their true values and stop decreasing over time.
          </div>
        </div>

        <div className="glass-card p-4 border-l-4 border-amber-400 bg-amber-50/40 space-y-3">
          <div className="text-xs text-slate-700 space-y-1">
            <div><b>What it does</b> — restores every row where <code>credit_reason = &quot;superseded — later call attributed the conversion&quot;</code>, re-runs dedup with the new (brand, customer, credit_date_ist) key, rebuilds main_daily_agg for the affected date range, wipes the analytics snapshot cache.</div>
            <div><b>Scope</b> — all history.</div>
            <div><b>Duration</b> — 5-15 min depending on volume. Read-heavy but safe live.</div>
            <div><b>Idempotent</b> — running twice is a no-op (nothing to restore the second time).</div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={runRepair}
              disabled={repairLoading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="repair-rd-btn"
            >
              {repairLoading
                ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running… ({Math.round((Date.now() - (repairStarted || Date.now())) / 1000)}s)</>
                : <>Run backfill now</>}
            </Button>
            {repairResult && (
              <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                ✓ Completed in {Math.round(((Date.now() - (repairStarted || Date.now())) / 1000))}s
              </span>
            )}
          </div>

          {repairError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2"
                 data-testid="repair-rd-error">
              {repairError}
            </div>
          )}

          {repairResult && (
            <div className="space-y-2" data-testid="repair-rd-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-white/70 rounded p-2 border border-slate-200">
                  <div className="text-[10px] uppercase text-slate-500">Candidates scanned</div>
                  <div className="text-lg font-semibold text-slate-900">
                    {(repairResult.candidates_scanned || 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] text-slate-400">
                    of {(repairResult.candidates_total || 0).toLocaleString()}
                  </div>
                </div>
                <div className="bg-white/70 rounded p-2 border border-slate-200">
                  <div className="text-[10px] uppercase text-slate-500">Rows restored</div>
                  <div className="text-lg font-semibold text-emerald-700">
                    {(repairResult.restored || 0).toLocaleString()}
                  </div>
                </div>
                <div className="bg-white/70 rounded p-2 border border-slate-200">
                  <div className="text-[10px] uppercase text-slate-500">Brands touched</div>
                  <div className="text-lg font-semibold text-slate-900">
                    {repairResult.brands_touched?.length || 0}
                  </div>
                </div>
                <div className="bg-white/70 rounded p-2 border border-slate-200">
                  <div className="text-[10px] uppercase text-slate-500">Snapshots wiped</div>
                  <div className="text-lg font-semibold text-slate-900">
                    {repairResult.snapshots_wiped || 0}
                  </div>
                </div>
              </div>
              {repairResult.credit_date_range?.min && (
                <div className="text-xs text-slate-600">
                  Restored credits span <b>{repairResult.credit_date_range.min}</b> → <b>{repairResult.credit_date_range.max}</b>.
                  Main rollup rebuilt for this range.
                </div>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-500 hover:text-slate-900">Full response JSON</summary>
                <pre className="mt-2 p-2 bg-slate-50 rounded max-h-64 overflow-auto text-[10px] leading-tight">
                  {JSON.stringify(repairResult, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
      </div>

      {/* ---- Diagnose a user ---- */}
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">Diagnose a user</h1>
          <div className="text-sm text-slate-600 mt-1">
            See every call this customer received and exactly why credit was (or was not) awarded.
          </div>
        </div>
        <div className="glass-card p-4 space-y-3">
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wide">Customer name(s)</label>
            <textarea
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Enter one or more usernames, separated by commas or new lines&#10;e.g.  alice, bob123, charlie_d"
              rows={3}
              className="glass-input w-full mt-1 py-2 px-3 text-sm resize-y"
              data-testid="diagnose-user-input"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Tip: paste a comma or newline-separated list. Names are matched case-insensitively.
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs text-slate-500 uppercase tracking-wide">Brand ID (optional)</label>
              <input
                type="number"
                value={userBrand}
                onChange={(e) => setUserBrand(e.target.value)}
                placeholder="e.g. 39"
                className="glass-input h-9 w-32 mt-1"
                data-testid="diagnose-user-brand"
              />
            </div>
            <Button
              onClick={runUser}
              disabled={userLoading || !userQuery.trim()}
              data-testid="diagnose-user-btn"
              className="bg-[#0A84FF] hover:bg-[#0069cc]"
            >
              {userLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Looking up…</> :
                <><Search className="w-4 h-4 mr-2" /> Diagnose</>}
            </Button>
          </div>
        </div>

        {userError && (
          <div className="glass-card p-3 border-l-4 border-red-500 flex gap-2 items-start">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-slate-700">{userError}</div>
          </div>
        )}

        {userData && (
          userData.call_count === 0 ? (
            <div className="glass-card p-4 text-sm text-slate-500 space-y-3"
                 data-testid="diagnose-user-no-calls">
              <div>
                No calls found for <span className="font-mono">{userData.customer_name}</span>
                {userData.brand_id != null ? ` on brand ${userData.brand_id}` : " on any brand"}.
              </div>
              {(userData.close_matches || []).length > 0 && (
                <div className="border-l-4 border-amber-400 bg-amber-50/60 rounded p-3">
                  <div className="text-xs font-semibold text-amber-900 mb-2">
                    Did you mean… <span className="text-amber-700 font-normal">({userData.close_matches.length} similar names in the DB)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {userData.close_matches.map((m) => (
                      <button
                        key={`${m.brand_id}::${m.customer_name}`}
                        type="button"
                        onClick={() => {
                          setUserQuery(m.customer_name);
                          setUserBrand(String(m.brand_id ?? ""));
                        }}
                        className="text-left px-2 py-1 rounded hover:bg-amber-100/60 transition-colors"
                        data-testid={`close-match-${m.customer_name}`}
                        title="Click to load this customer into the search"
                      >
                        <span className="font-mono text-slate-900">{m.customer_name}</span>
                        <span className="text-slate-500">
                          {" · brand "}{m.brand_id}
                          {m.campaign_name ? ` · ${m.campaign_name}` : ""}
                          {m.latest_attempt_date ? ` · last ${m.latest_attempt_date}` : ""}
                          {m.credit_status ? ` · ${m.credit_status}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="glass-card p-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <div><span className="text-slate-500">Requested:</span> <span className="font-medium">{userData.requested_names?.length || 1}</span> name(s)</div>
                <div><span className="text-slate-500">Users found:</span> <span className="font-medium">{userData.by_user?.length || 0}</span></div>
                <div><span className="text-slate-500">Total calls:</span> <span className="font-medium">{userData.call_count}</span></div>
                <div><span className="text-slate-500">Credited users:</span>{" "}
                  <span className="font-semibold text-emerald-700">
                    {(userData.by_user || []).filter((u) => !!u.credit_date).length}
                  </span>
                  <span className="text-slate-400 text-xs ml-1">
                    ({(userData.by_user || []).filter((u) => u.credit_status === "yes").length} call
                    {" · "}
                    {(userData.by_user || []).filter((u) => u.credit_status === "sms").length} sms
                    {" · "}
                    {(userData.by_user || []).filter((u) => u.credit_status === "unsure").length} unsure)
                  </span>
                </div>
                <div><span className="text-slate-500">Brands:</span> {userData.brands_seen.join(", ") || "—"}</div>
                <div><span className="text-slate-500">Campaigns:</span> {userData.campaigns_seen.join(", ") || "—"}</div>
                <div><span className="text-slate-500">Any credit=yes?</span>{" "}
                  {userData.any_credit_yes
                    ? <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                    : <span className="text-red-600 font-semibold inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> No</span>}
                </div>
                {userData.unmatched_names?.length > 0 && (
                  <div className="w-full text-amber-700 text-xs mt-1">
                    ⚠ No calls found for: <span className="font-mono">{userData.unmatched_names.join(", ")}</span>
                  </div>
                )}
              </div>

              {/* Credit-date vs Deposit-date drift — shows WHERE the credit
                  is being bucketed by day. If the "Credit date" column has
                  entries in early July but the "Deposit date" column is all
                  July 7, that's the legacy-fallback bug in action and the
                  fix is running Deep MySQL re-enrichment. */}
              {(userData.credit_by_date?.length > 0 || userData.deposit_by_date?.length > 0) && (
                <div className="glass-card p-3 overflow-x-auto border-l-4 border-amber-500"
                     data-testid="diagnose-credit-vs-deposit-drift">
                  <div className="font-outfit font-semibold text-slate-900 mb-1 text-sm">
                    Credit date vs Deposit date — where did the credit land?
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    <b>Credit date</b> = the day the ops rollup bucketed this credit (drives KPI /
                    Breakdown / Pivot). <b>Deposit date</b> = the day the customer actually deposited.
                    If they differ, your legacy rows haven&apos;t been re-enriched from MySQL yet — run
                    the fuchsia &ldquo;Deep re-enrich&rdquo; card below to align them.
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs font-medium text-slate-700 mb-1">Credit by date (rollup bucket)</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200/60">
                            <th className="py-1 px-2 font-medium">Date</th>
                            <th className="py-1 px-2 font-medium text-right">Total</th>
                            <th className="py-1 px-2 font-medium text-right text-emerald-700">yes</th>
                            <th className="py-1 px-2 font-medium text-right text-blue-700">sms</th>
                            <th className="py-1 px-2 font-medium text-right text-purple-700">wn</th>
                            <th className="py-1 px-2 font-medium text-right text-red-700">inv</th>
                            <th className="py-1 px-2 font-medium text-right text-amber-700">?</th>
                            <th className="py-1 px-2 font-medium text-right text-slate-400">no</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(userData.credit_by_date || []).map((r) => (
                            <tr key={r.date} className="border-t border-white/60">
                              <td className="py-1 px-2 font-mono">{r.date}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.total}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.yes || 0}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.sms || 0}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.wn || 0}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.invalid || 0}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.unsure || 0}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.no || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-slate-700 mb-1">
                        Deposit by date (where credit SHOULD go after re-enrich)
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200/60">
                            <th className="py-1 px-2 font-medium">Date</th>
                            <th className="py-1 px-2 font-medium text-right">Credited users depositing here</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(userData.deposit_by_date || []).map((r) => (
                            <tr key={r.date} className="border-t border-white/60">
                              <td className="py-1 px-2 font-mono">{r.date}</td>
                              <td className="py-1 px-2 text-right font-mono">{r.credited_users_that_deposited_here}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}


              {/* Per-(user × brand) summary — the "bulk view" */}
              {userData.by_user?.length > 1 && (
                <div className="glass-card p-3 overflow-x-auto" data-testid="diagnose-user-by-user">
                  <div className="font-outfit font-semibold text-slate-900 mb-2 text-sm">
                    Summary by user × brand ({userData.by_user.length} rows)
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 text-[10px] uppercase">
                        <th className="text-left py-1 px-2">Customer</th>
                        <th className="text-right py-1 px-2">Brand</th>
                        <th className="text-right py-1 px-2">Calls</th>
                        <th className="text-right py-1 px-2">Credited</th>
                        <th className="text-right py-1 px-2">Reactivated?</th>
                        <th className="text-left py-1 px-2">Campaigns</th>
                        <th className="text-left py-1 px-2">First call</th>
                        <th className="text-left py-1 px-2">Last call</th>
                        <th className="text-left py-1 px-2" title="Call date (when the credited call was made) → Deposit date (when the customer actually deposited).">Credit call → deposit</th>
                        <th className="text-left py-1 px-2">Latest reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userData.by_user.map((u, i) => (
                        <tr key={i} className="border-t border-white/60">
                          <td className="py-1 px-2 font-medium">{u.customer_name}</td>
                          <td className="py-1 px-2 text-right">{u.brand_id ?? "—"}</td>
                          <td className="py-1 px-2 text-right">{u.call_count}</td>
                          <td className={`py-1 px-2 text-right font-semibold ${(u.credit_positive_count ?? u.credit_yes_count) > 0 ? "text-emerald-700" : "text-red-600"}`}
                              title={`yes=${u.credit_yes_count} sms=${u.credit_sms_count ?? 0} unsure=${u.credit_unsure_count ?? 0}`}>
                            {u.credit_positive_count ?? u.credit_yes_count}
                          </td>
                          <td className="py-1 px-2 text-right">{u.any_reactivated ? "yes" : "no"}</td>
                          <td className="py-1 px-2 truncate max-w-[220px]" title={u.campaigns.join(", ")}>{u.campaigns.join(", ") || "—"}</td>
                          <td className="py-1 px-2 font-mono text-[10px]">{u.first_call || "—"}</td>
                          <td className="py-1 px-2 font-mono text-[10px]">{u.last_call || "—"}</td>
                          <td className="py-1 px-2 font-mono text-[10px]">
                            {u.credit_date ? (
                              <div className="space-y-0.5" title={`credit_status = ${u.credit_status || "n/a"}`}>
                                <div className={
                                  u.credit_status === "yes"    ? "text-emerald-700 font-semibold" :
                                  u.credit_status === "sms"    ? "text-blue-700 font-semibold" :
                                  u.credit_status === "unsure" ? "text-amber-700 font-semibold" :
                                                                 "text-slate-500"
                                }>
                                  📞 {u.credit_date}
                                  {u.credit_status ? ` · ${u.credit_status}` : ""}
                                </div>
                                {u.deposit_date && (
                                  <div className="text-slate-500">💰 {u.deposit_date}</div>
                                )}
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-1 px-2 italic text-slate-600 whitespace-normal break-words max-w-[420px] leading-snug" title={u.last_credit_reason || ""}>
                            {u.last_credit_reason || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Per-call detail — kept as expandable/details if the list is huge */}
              {userData.calls.length > 25 ? (
                <details className="glass-card p-3">
                  <summary className="cursor-pointer text-sm text-slate-700 font-medium">
                    Show all {userData.calls.length} individual calls
                  </summary>
                  <div className="space-y-3 mt-3">
                    {userData.calls.map((c, i) => (
                      <CallCard key={i} c={c} i={i} />
                    ))}
                  </div>
                </details>
              ) : (
                userData.calls.map((c, i) => <CallCard key={i} c={c} i={i} />)
              )}
            </div>
          )
        )}
      </div>

      <div className="border-t border-slate-200" />

      {/* Explainer card visible to everyone (admins + ops) */}
      <div className="glass-card p-4 text-xs text-slate-700 space-y-2">
        <div className="font-semibold text-slate-900 text-sm">How Fresh / Follow-up is decided</div>
        <div>
          A call is <span className="font-semibold text-emerald-700">Fresh</span> only if this customer has{" "}
          never been called before <span className="italic">on this brand</span> (across all uploads ever).
          Every subsequent call to the same customer on the same brand is a{" "}
          <span className="font-semibold text-blue-700">Follow-up</span> — regardless of whether the previous
          disposition was &quot;Interested&quot;, &quot;No Answer&quot;, or anything else.
        </div>
        <div className="text-slate-600">
          Scope is <code>(brand_id, customer_name_lower)</code>. Same customer on a different brand is Fresh
          again for that brand. Guarantee: <code>Fresh + Follow-up = Total calls</code> — no invisible bucket.
        </div>
        <div className="text-slate-600">
          Row counts you see on the dashboard may differ from your Excel row count because we skip{" "}
          <span className="font-mono">blank rows</span>, rows with unparseable{" "}
          <span className="font-mono">attempt_datetime</span>, and de-duplicate rows that share the same{" "}
          <span className="font-mono">(brand · customer · attempt_datetime)</span> key. Open a specific upload
          under <code>/ops/uploads/&lt;id&gt;</code> to see the exact row-math for that file.
        </div>
      </div>

      {/* ---- Admin-only sections below ---- */}
      {isAdmin && (
      <>
      {/* Explainer card that everyone (including ops) should see */}
      <div className="glass-card p-4 text-xs text-slate-700 space-y-2">
        <div className="font-semibold text-slate-900 text-sm">How Fresh / Follow-up is decided</div>
        <div>
          A call is <span className="font-semibold text-emerald-700">Fresh</span> only if this customer has{" "}
          never been called before <span className="italic">on this brand</span> (across all uploads ever).
          Every subsequent call to the same customer on the same brand is a{" "}
          <span className="font-semibold text-blue-700">Follow-up</span> — regardless of whether the previous
          disposition was &quot;Interested&quot;, &quot;No Answer&quot;, or anything else.
        </div>
        <div className="text-slate-600">
          Scope is <code>(brand_id, customer_name_lower)</code>. Same customer on a different brand is Fresh
          again for that brand. Guarantee: <code>Fresh + Follow-up = Total calls</code> — no invisible bucket.
        </div>
        <div className="text-slate-600">
          Row counts you see on the dashboard may differ from your Excel row count because we skip{" "}
          <span className="font-mono">blank rows</span>, rows with unparseable{" "}
          <span className="font-mono">attempt_datetime</span>, and de-duplicate rows that share the same{" "}
          <span className="font-mono">(brand · customer · attempt_datetime)</span> key. Open a specific upload
          under <code>/ops/uploads/&lt;id&gt;</code> to see the exact row-math for that file.
        </div>
      </div>
      {/* ---- Ops-date diagnostics (existing) ---- */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-slate-900">
            Ops-date diagnostics
          </h1>
          <div className="text-sm text-slate-600 mt-1">
            Read-only snapshot: shows how <code>attempt_date_ist</code> is populated on ops_call_rows.
            Use this when a specific date-filter returns zero.
          </div>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          data-testid="run-diagnose-btn"
          className="bg-[#0A84FF] hover:bg-[#0069cc]"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running…</> :
            <><RefreshCw className="w-4 h-4 mr-2" /> Run diagnostic</>}
        </Button>
      </div>

      {/* Corrective one-time backfill for the DD/MM/YYYY parser bug */}
      <div className="glass-card p-4 border-l-4 border-amber-500">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Fix DD/MM/YYYY date parsing</div>
            <div className="text-sm text-slate-600 mt-1">
              If your Excel exports use Indian date format (e.g. <code>01/07/2026</code> for
              July&nbsp;1), older rows may have been stamped with a wrong{" "}
              <code>attempt_date_ist</code> (e.g. Jan&nbsp;7). Run this one-time corrective
              backfill to re-parse every row with the fixed parser. Safe to run repeatedly;
              rows already correct are skipped.
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                onClick={runFix}
                disabled={fixLoading}
                data-testid="run-fix-attempt-date-btn"
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {fixLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Re-stamping…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Re-stamp all rows</>}
              </Button>
              {fixResult && (
                <div className="text-sm text-emerald-700">
                  ✓ Scanned {fixResult.scanned.toLocaleString()} · restamped{" "}
                  <span className="font-semibold">{fixResult.restamped.toLocaleString()}</span> rows.
                </div>
              )}
              {fixError && <div className="text-sm text-red-600">{fixError}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Credit reasons breakdown */}
      <div className="glass-card p-4 border-l-4 border-blue-500" data-testid="credit-reasons-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Credit reasons breakdown</div>
            <div className="text-sm text-slate-600 mt-1">
              Full histogram of <code>credit_status</code> × <code>credit_reason</code> across all
              ops_call_rows. Answers: <em>&quot;Why isn&apos;t X getting credit?&quot;</em>. Optional filters below scope
              the result to a date range or brand.
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div>
                <label className="text-xs text-slate-500 uppercase">From</label>
                <input type="date" value={reasonsFrom}
                       onChange={(e) => setReasonsFrom(e.target.value)}
                       className="glass-input h-9 w-full mt-1"
                       data-testid="credit-reasons-from"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase">To</label>
                <input type="date" value={reasonsTo}
                       onChange={(e) => setReasonsTo(e.target.value)}
                       className="glass-input h-9 w-full mt-1"
                       data-testid="credit-reasons-to"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase">Brand id (optional)</label>
                <input type="number" value={reasonsBrand}
                       onChange={(e) => setReasonsBrand(e.target.value)}
                       className="glass-input h-9 w-full mt-1 font-mono"
                       placeholder="e.g. 39"
                       data-testid="credit-reasons-brand"/>
              </div>
              <Button onClick={runReasons} disabled={reasonsLoading}
                      data-testid="run-credit-reasons-btn"
                      className="bg-blue-600 hover:bg-blue-700 text-white">
                {reasonsLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Load breakdown</>}
              </Button>
            </div>
            {reasonsError && <div className="text-sm text-red-600 mt-2">{reasonsError}</div>}
            {reasonsData && (
              <div className="mt-4">
                <div className="text-xs text-slate-500 mb-2">
                  {reasonsData.total_rows.toLocaleString()} rows analyzed.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {Object.entries(reasonsData.by_status).map(([status, bkt]) => (
                    <div key={status} className="border rounded-lg p-3 bg-white/60"
                         data-testid={`reasons-bucket-${status}`}>
                      <div className="flex items-baseline justify-between mb-2">
                        <div className="font-semibold uppercase text-xs">
                          <span className={
                            status === "yes"     ? "text-emerald-700" :
                            status === "sms"     ? "text-blue-700" :
                            status === "wn"      ? "text-purple-700" :
                            status === "invalid" ? "text-pink-700" :
                            status === "unsure"  ? "text-amber-700" :
                                                   "text-red-700"
                          }>
                            {status === "wn" ? "Wrong number"
                              : status === "invalid" ? "Invalid number"
                              : status}
                          </span>
                        </div>
                        <div className="text-lg font-bold font-mono">{bkt.total.toLocaleString()}</div>
                      </div>
                      {bkt.reasons.length === 0 && (
                        <div className="text-xs text-slate-400 italic">No rows.</div>
                      )}
                      <div className="space-y-1 text-xs">
                        {bkt.reasons.map((r, i) => (
                          <div key={i} className="flex justify-between gap-2">
                            <span className="text-slate-700 italic break-words flex-1">{r.reason}</span>
                            <span className="font-mono font-semibold text-slate-900 shrink-0">
                              {r.count.toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Retag SMS-eligible dispositions */}

      {/* Backfill credit_date_ist + switch to deposit-date bucketing */}
      <div className="glass-card p-4 border-l-4 border-purple-500" data-testid="backfill-credit-date-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Full credit recalculation + deposit-date bucketing</div>
            <div className="text-sm text-slate-600 mt-1">
              The nuclear one-click reclassification. For EVERY row in{" "}
              <code>ops_call_rows</code>: re-runs <code>compute_credit()</code>{" "}
              with today&apos;s rules (updated SMS-eligible dispositions,
              cooldown, deposit window), sets <code>credit_date_ist</code>,
              re-runs dedup, and rebuilds both rollups. Dashboards will bucket
              conversions by DEPOSIT DATE. Safe to re-run — idempotent.
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                onClick={runBackfillCreditDate}
                disabled={bfLoading}
                data-testid="run-backfill-credit-date-btn"
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {bfLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Recomputing everything…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Recompute all credit + rebuild</>}
              </Button>
              {bfResult && (
                <div className="text-sm text-emerald-700 flex flex-wrap gap-x-3">
                  <span>✓ Scanned {bfResult.scanned?.toLocaleString?.() ?? bfResult.scanned}</span>
                  <span>· Updated <span className="font-semibold">{bfResult.updated?.toLocaleString?.() ?? bfResult.updated}</span></span>
                  <span>· Status changed <span className="font-semibold">{bfResult.changed_status?.toLocaleString?.() ?? bfResult.changed_status}</span></span>
                  <span>· Brands {bfResult.brands_touched}</span>
                  <span>· Customers {bfResult.customers_touched?.toLocaleString?.() ?? bfResult.customers_touched}</span>
                  <span>· Elapsed {bfResult.elapsed_secs}s</span>
                </div>
              )}
              {bfError && <div className="text-sm text-red-600">{bfError}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Diagnose one user's credit attribution */}
      <div className="glass-card p-4 border-l-4 border-cyan-500" data-testid="diagnose-user-card">
        <div className="flex items-start gap-3">
          <Search className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Diagnose a single user&apos;s credit</div>
            <div className="text-sm text-slate-600 mt-1">
              Returns the raw ops_call_row(s), where the user shows up in the
              rollup, the campaign config, and a per-day credit histogram for
              the brand — so you can trace exactly why an aggregate number is
              what it is.
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-slate-500">Brand ID</label>
                <input className="glass-input mt-1 w-full text-sm" placeholder="e.g. 39"
                  value={dxBrand} onChange={(e) => setDxBrand(e.target.value)}
                  data-testid="dx-brand-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Username (lowercase)</label>
                <input className="glass-input mt-1 w-full text-sm" placeholder="pradeepsoni1234"
                  value={dxUser} onChange={(e) => setDxUser(e.target.value)}
                  data-testid="dx-username-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">From</label>
                <input type="date" className="glass-input mt-1 w-full text-sm"
                  value={dxFrom} onChange={(e) => setDxFrom(e.target.value)}
                  data-testid="dx-from-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">To</label>
                <input type="date" className="glass-input mt-1 w-full text-sm"
                  value={dxTo} onChange={(e) => setDxTo(e.target.value)}
                  data-testid="dx-to-input" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                onClick={runDiagnoseUser}
                disabled={dxLoading || !dxBrand.trim() || !dxUser.trim()}
                data-testid="run-diagnose-user-btn"
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {dxLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Diagnosing…</>
                  : <><Search className="w-4 h-4 mr-2" /> Diagnose user</>}
              </Button>
              {dxError && <div className="text-sm text-red-600">{dxError}</div>}
            </div>
            {dxData && (
              <div className="mt-3 space-y-2">
                <div className="text-xs text-slate-500">
                  {dxData.raw_rows_count} raw call row(s) ·{" "}
                  {dxData.credited_rows_in_window?.by_credit_date ?? 0} credited in window (credit-date basis) ·{" "}
                  {dxData.credited_rows_in_window?.by_attempt_date ?? 0} (attempt-date basis)
                </div>
                <pre className="bg-slate-950 text-slate-100 text-[11px] font-mono p-3 rounded-lg max-h-96 overflow-auto whitespace-pre-wrap">
{JSON.stringify(dxData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Re-enrichment status — shows continuous job cadence + last run
          summary (when, what date range, how many brands touched, how
          many users converted in the window). Polls every 5s while page
          is open. */}
      {reStatus && (
        <div className="glass-card p-4 border-l-4 border-emerald-500" data-testid="reenrich-status-card">
          <div className="flex items-start gap-3">
            <RefreshCw className={`w-5 h-5 shrink-0 mt-0.5 ${reStatus.state?.status === "running" ? "text-emerald-600 animate-spin" : "text-emerald-600"}`} />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">
                  Re-enrichment status
                </div>
                <span className="text-xs text-slate-500" title={reStatus.window?.explain || ""}>
                  {reStatus.enabled ? "Auto" : "Auto (disabled)"} every {reStatus.interval_hours}h
                  {" · "}
                  last {reStatus.window?.days ?? reStatus.window_days ?? 12} days, all brands
                  {reStatus.window?.kind === "dynamic" && (
                    <span className="ml-1 text-slate-400">
                      (= {reStatus.window.max_allowed_period_after_call}d campaign window + {reStatus.window.safety_buffer_days}d buffer)
                    </span>
                  )}
                </span>
              </div>
              {reStatus.state?.status === "running" && (
                <div className="mt-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                  Running now · started {reStatus.state.started_at?.slice(11, 19)} UTC · brands done{" "}
                  <b>{reStatus.state.progress_brands_done || 0}</b> · scanned{" "}
                  <b>{(reStatus.state.progress_scanned || 0).toLocaleString()}</b> · updated{" "}
                  <b>{(reStatus.state.progress_updated || 0).toLocaleString()}</b>
                </div>
              )}
              {reStatus.state?.status === "failed" && (
                <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                  <XCircle className="w-4 h-4 inline mr-1" />
                  Last run FAILED — {reStatus.state.error}
                </div>
              )}
              {reStatus.state?.last_run_at && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Last run</div>
                    <div className="font-mono">{new Date(reStatus.state.last_run_at).toLocaleString()}</div>
                    <div className="text-[11px] text-slate-500">{reStatus.state.last_run_kind || "manual"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Window covered</div>
                    <div className="font-mono">{reStatus.state.last_date_from} → {reStatus.state.last_date_to}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Brands · Rows updated</div>
                    <div className="font-mono">
                      {reStatus.state.last_brands_processed || 0} · {(reStatus.state.last_updated || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      scanned {(reStatus.state.last_scanned || 0).toLocaleString()} · {reStatus.state.last_elapsed_secs || 0}s
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Users converted</div>
                    <div className="font-mono text-emerald-700 font-semibold">
                      {(reStatus.state.last_users_converted_in_window || 0).toLocaleString()}
                    </div>
                    <div className="text-[11px] text-slate-500">in window</div>
                  </div>
                  {(reStatus.state.last_held_for_review || 0) > 0 && (
                    <div className="col-span-2 md:col-span-1">
                      <div className="text-[10px] uppercase text-amber-700 font-medium">Held for review</div>
                      <div className="font-mono text-amber-700 font-semibold">
                        {(reStatus.state.last_held_for_review || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-amber-700/80">
                        credit-strips skipped — run Preview then <b>Apply all + reviewed strips</b> after spot-checking
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!reStatus.state?.last_run_at && reStatus.state?.status !== "running" && (
                <div className="mt-2 text-sm text-slate-500 italic">
                  No re-enrichment has run yet. Continuous refresher kicks in every {reStatus.interval_hours}h,
                  or trigger a manual one below.
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Deep MySQL re-enrichment: fixes credit_date_ist for legacy rows
          by re-querying payment DB for FIRST post-call deposit. This is the
          ONLY way historical rows can bucket on their true deposit day
          (e.g., pradeep July 1 instead of June 30 fallback). */}
      <div className="glass-card p-4 border-l-4 border-fuchsia-500" data-testid="reenrich-mysql-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-fuchsia-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Deep re-enrich from MySQL (preview → apply → undo)</div>
            <div className="text-sm text-slate-600 mt-1">
              For every row in the chosen <b>date range</b> (and optionally a
              specific brand — leave blank for <b>ALL brands</b>), re-queries
              the payment MySQL to repopulate every deposit field including{" "}
              <code>first_deposit_after_date</code>, then reruns{" "}
              <code>compute_credit</code>. <b>You&apos;ll see the exact diff</b> in a
              dialog — how many rows gain / lose credit, sample rows per
              transition, and a one-click <b>Undo</b> after apply. Nothing is
              written to <code>ops_call_rows</code> until you click Apply.
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-slate-500">Brand ID (blank = all brands)</label>
                <input className="glass-input mt-1 w-full text-sm" placeholder="e.g. 39 or leave blank"
                  value={reBrand} onChange={(e) => setReBrand(e.target.value)}
                  data-testid="reenrich-brand-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">From</label>
                <input type="date" className="glass-input mt-1 w-full text-sm"
                  value={reFrom} onChange={(e) => setReFrom(e.target.value)}
                  data-testid="reenrich-from-input" />
              </div>
              <div>
                <label className="text-xs text-slate-500">To</label>
                <input type="date" className="glass-input mt-1 w-full text-sm"
                  value={reTo} onChange={(e) => setReTo(e.target.value)}
                  data-testid="reenrich-to-input" />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <ReenrichPreviewButton
                brandId={reBrand.trim() ? Number(reBrand) : null}
                dateFrom={reFrom}
                dateTo={reTo}
              />
              <button
                onClick={runReenrich}
                disabled={reLoading || !reFrom || !reTo}
                data-testid="run-reenrich-mysql-btn"
                className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 disabled:opacity-40 disabled:no-underline"
                title="Skip preview — apply directly. Only use for scheduled/automation flows."
              >
                {reLoading
                  ? "Applying directly…"
                  : "Advanced: apply without preview"}
              </button>
              {reResult && (
                <div className="text-sm text-emerald-700">
                  ✓ {reResult.status === "started"
                       ? "Started in background — watch the status card above for progress"
                       : `Brands ${reResult.brands_processed ?? 1} · Scanned ${reResult.scanned?.toLocaleString?.() ?? reResult.scanned} · Updated ${reResult.updated?.toLocaleString?.() ?? reResult.updated} · Elapsed ${reResult.elapsed_secs}s`}
                </div>
              )}
              {reError && <div className="text-sm text-red-600">{reError}</div>}
            </div>
          </div>
        </div>
      </div>




      <div className="glass-card p-4 border-l-4 border-emerald-500" data-testid="retag-sms-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Grant SMS credit for &quot;no answer&quot; &amp; unreachable rows</div>
            <div className="text-sm text-slate-600 mt-1">
              Reclassifies historical rows currently at <code>credit_status=&quot;no&quot;</code> whose
              disposition looks like <code>no answer</code>, <code>unreachable</code>, <code>busy</code>,{" "}
              <code>voicemail</code>, or <code>language barrier</code>. Grants them{" "}
              <span className="font-semibold text-emerald-700">SMS credit</span> (subject to campaign
              cooldown + deposit-window rules), re-runs dedup so the latest call wins attribution,
              and rebuilds daily rollups. Idempotent — safe to re-run.
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Button
                onClick={runRetagSms}
                disabled={retagLoading}
                data-testid="run-retag-sms-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {retagLoading
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Reclassifying…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Retag SMS-eligible rows</>}
              </Button>
              {retagResult && (
                <div className="text-sm text-emerald-700 space-x-3">
                  <span>✓ Candidates {retagResult.candidates?.toLocaleString?.() ?? retagResult.candidates}</span>
                  <span>· Reclassified <span className="font-semibold">{retagResult.reclassified?.toLocaleString?.() ?? retagResult.reclassified}</span></span>
                  <span>· Brands touched {retagResult.brands_touched}</span>
                </div>
              )}
              {retagError && <div className="text-sm text-red-600">{retagError}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Purge by date range — nuclear cleanup for stale dashboard data */}
      <div className="glass-card p-4 border-l-4 border-red-500" data-testid="purge-by-date-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Purge dashboard rows by date range</div>
            <div className="text-sm text-slate-600 mt-1">
              Deletes ALL rows in the date range from <code>ops_call_rows</code> AND{" "}
              <code>report_rows</code> — regardless of which upload they came from.
              Uploads / reports whose entire row-set is wiped are also cleaned up. Use this
              when the dashboard still shows data for days you thought you&apos;d deleted (typically
              because multiple uploads covered the same date, or orphaned rows exist without a
              valid <code>upload_id</code>).
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">From (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={purgeFrom}
                  onChange={(e) => { setPurgeFrom(e.target.value); setPurgePreview(null); setPurgeResult(null); }}
                  className="glass-input h-9 w-full mt-1"
                  data-testid="purge-date-from"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">To (YYYY-MM-DD)</label>
                <input
                  type="date"
                  value={purgeTo}
                  onChange={(e) => { setPurgeTo(e.target.value); setPurgePreview(null); setPurgeResult(null); }}
                  className="glass-input h-9 w-full mt-1"
                  data-testid="purge-date-to"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 uppercase tracking-wide">Brand ID (optional)</label>
                <input
                  type="number"
                  value={purgeBrand}
                  onChange={(e) => { setPurgeBrand(e.target.value); setPurgePreview(null); setPurgeResult(null); }}
                  placeholder="all brands"
                  className="glass-input h-9 w-full mt-1"
                  data-testid="purge-brand"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button
                  onClick={runPurgePreview}
                  disabled={purgeLoading || !purgeFrom || !purgeTo}
                  data-testid="purge-preview-btn"
                  className="bg-slate-600 hover:bg-slate-700 text-white"
                >
                  {purgeLoading && !purgePreview ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Preview…</> : "Preview"}
                </Button>
                {purgePreview && (
                  <Button
                    onClick={runPurgeConfirm}
                    disabled={purgeLoading}
                    data-testid="purge-confirm-btn"
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {purgeLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Purging…</> : "Confirm delete"}
                  </Button>
                )}
              </div>
            </div>

            {purgePreview && (
              <div className="mt-3 glass-solid p-3 text-sm" data-testid="purge-preview-result">
                <div className="font-medium text-slate-900 mb-1">Dry-run preview:</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div><span className="text-slate-500">ops_call_rows:</span> <span className="font-semibold">{purgePreview.would_delete.ops_call_rows.toLocaleString()}</span></div>
                  <div><span className="text-slate-500">report_rows:</span> <span className="font-semibold">{purgePreview.would_delete.report_rows.toLocaleString()}</span></div>
                  <div><span className="text-slate-500">Uploads touched:</span> <span className="font-semibold">{purgePreview.would_delete.ops_upload_ids_touched.length}</span></div>
                  <div><span className="text-slate-500">Reports touched:</span> <span className="font-semibold">{purgePreview.would_delete.report_ids_touched.length}</span></div>
                </div>
                <div className="text-[11px] text-slate-500 mt-2">
                  Click &quot;Confirm delete&quot; to run the actual purge. Uploads whose entire row-set is wiped will be fully removed.
                </div>
              </div>
            )}

            {purgeResult && (
              <div className="mt-3 glass-solid p-3 text-sm text-emerald-800 bg-emerald-50/50" data-testid="purge-done-result">
                <div className="font-medium mb-1">✓ Purge complete</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>ops_call_rows deleted: <b>{(purgeResult.deleted?.ops_call_rows || 0).toLocaleString()}</b></div>
                  <div>report_rows deleted: <b>{(purgeResult.deleted?.report_rows || 0).toLocaleString()}</b></div>
                  <div>Uploads removed: <b>{purgeResult.deleted?.ops_uploads_removed || 0}</b></div>
                  <div>Reports removed: <b>{purgeResult.deleted?.reports_removed || 0}</b></div>
                </div>
              </div>
            )}

            {purgeError && (
              <div className="mt-3 text-sm text-red-600" data-testid="purge-error">{purgeError}</div>
            )}
          </div>
        </div>
      </div>

      {/* Audit: dashboard rows vs live uploads/reports — cross-check */}
      <div className="glass-card p-4 border-l-4 border-blue-500" data-testid="audit-card">
        <div className="flex items-start gap-3">
          <Search className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">
              Audit: dashboard rows vs live uploads / reports
            </div>
            <div className="text-sm text-slate-600 mt-1">
              For every date currently visible on the dashboard, checks which
              <code>upload_id</code> / <code>report_id</code> the rows belong to and marks each as
              <b> ALIVE</b> (parent found), <b>ORPHAN</b> (parent deleted but rows survived),
              or <b>NULL</b> (legacy row with no parent id). Any date showing data but with
              zero alive parents is a &quot;ghost date&quot; — the exact thing you were seeing.
            </div>
            <div className="mt-3 flex gap-2 flex-wrap items-center">
              <Button
                onClick={runAudit}
                disabled={auditLoading}
                data-testid="audit-run-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {auditLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Running audit…</> : "Run audit"}
              </Button>
              {auditData && (auditData.summary.orphan_rows_total > 0 || auditData.summary.null_parent_rows_total > 0) && (
                <Button
                  onClick={purgeOrphans}
                  disabled={orphanBusy}
                  data-testid="audit-purge-orphans-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {orphanBusy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Cleaning…</> : "Delete all orphans + rebuild rollup"}
                </Button>
              )}
            </div>

            {auditError && (
              <div className="mt-3 text-sm text-red-600" data-testid="audit-error">{auditError}</div>
            )}

            {auditData && (
              <div className="mt-3 space-y-3" data-testid="audit-result">
                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="glass-solid p-2">
                    <div className="text-slate-500 text-[10px] uppercase">ops_call_rows</div>
                    <div className="text-lg font-semibold">{auditData.summary.total_ops_call_rows.toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500">from {auditData.summary.alive_ops_uploads} live uploads</div>
                  </div>
                  <div className="glass-solid p-2">
                    <div className="text-slate-500 text-[10px] uppercase">report_rows</div>
                    <div className="text-lg font-semibold">{auditData.summary.total_report_rows.toLocaleString()}</div>
                    <div className="text-[10px] text-slate-500">from {auditData.summary.alive_reports} live reports</div>
                  </div>
                  <div className={`glass-solid p-2 ${auditData.summary.orphan_rows_total > 0 ? "bg-amber-50" : ""}`}>
                    <div className="text-slate-500 text-[10px] uppercase">Orphan rows</div>
                    <div className={`text-lg font-semibold ${auditData.summary.orphan_rows_total > 0 ? "text-amber-700" : ""}`}>
                      {auditData.summary.orphan_rows_total.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500">parent was deleted</div>
                  </div>
                  <div className={`glass-solid p-2 ${auditData.summary.null_parent_rows_total > 0 ? "bg-amber-50" : ""}`}>
                    <div className="text-slate-500 text-[10px] uppercase">Null-parent rows</div>
                    <div className={`text-lg font-semibold ${auditData.summary.null_parent_rows_total > 0 ? "text-amber-700" : ""}`}>
                      {auditData.summary.null_parent_rows_total.toLocaleString()}
                    </div>
                    <div className="text-[10px] text-slate-500">legacy — no upload_id</div>
                  </div>
                </div>

                {/* Ghost dates callout */}
                {auditData.summary.ghost_dates_count > 0 && (
                  <div className="glass-solid p-3 bg-red-50/60 border-l-4 border-red-500" data-testid="audit-ghost-dates">
                    <div className="text-sm font-semibold text-red-800">
                      🚨 {auditData.summary.ghost_dates_count} ghost date{auditData.summary.ghost_dates_count === 1 ? "" : "s"} found
                    </div>
                    <div className="text-xs text-slate-700 mt-1">
                      These dates still show on the dashboard but have <b>zero</b> alive uploads/reports.
                      This is the &quot;why is X date still there?&quot; you were seeing.
                    </div>
                    <div className="mt-1 text-xs font-mono">
                      {auditData.ghost_dates.join(", ")}
                    </div>
                  </div>
                )}

                {/* Per-date detail table */}
                <details>
                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                    Per-date breakdown ({auditData.per_date.length} date{auditData.per_date.length === 1 ? "" : "s"}) — click to expand
                  </summary>
                  <div className="mt-2 max-h-80 overflow-y-auto border border-slate-200 rounded">
                    <table className="w-full text-[11px]">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-right px-2 py-1">ops_call_rows</th>
                          <th className="text-right px-2 py-1">orphan</th>
                          <th className="text-right px-2 py-1">null-parent</th>
                          <th className="text-right px-2 py-1">report_rows</th>
                          <th className="text-right px-2 py-1">orphan</th>
                          <th className="text-left px-2 py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditData.per_date.map((d) => (
                          <tr key={d.date} className={`border-t border-slate-100 ${d.is_ghost_date ? "bg-red-50/40" : ""}`}
                              data-testid={`audit-row-${d.date}`}>
                            <td className="px-2 py-1 font-mono">{d.date}</td>
                            <td className="px-2 py-1 text-right">{d.ops_rows.toLocaleString()}</td>
                            <td className={`px-2 py-1 text-right ${d.ops_orphan_rows > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{d.ops_orphan_rows.toLocaleString()}</td>
                            <td className={`px-2 py-1 text-right ${d.ops_null_upload_rows > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{d.ops_null_upload_rows.toLocaleString()}</td>
                            <td className="px-2 py-1 text-right">{d.report_rows.toLocaleString()}</td>
                            <td className={`px-2 py-1 text-right ${d.report_orphan_rows > 0 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{d.report_orphan_rows.toLocaleString()}</td>
                            <td className="px-2 py-1">
                              {d.is_ghost_date ? (
                                <span className="text-red-700 font-semibold">👻 ghost</span>
                              ) : (
                                <span className="text-emerald-700">✓ ok</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              </div>
            )}

            {orphanResult && (
              <div className="mt-3 glass-solid p-3 text-sm text-emerald-800 bg-emerald-50/50" data-testid="audit-orphan-cleaned">
                <div className="font-medium mb-1">✓ Orphans purged, rollup rebuilt</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div>ops rows (null upload): <b>{(orphanResult.deleted?.ops_call_rows_null_upload || 0).toLocaleString()}</b></div>
                  <div>ops rows (orphan): <b>{(orphanResult.deleted?.ops_call_rows_orphan || 0).toLocaleString()}</b></div>
                  <div>report rows (null): <b>{(orphanResult.deleted?.report_rows_null_report || 0).toLocaleString()}</b></div>
                  <div>report rows (orphan): <b>{(orphanResult.deleted?.report_rows_orphan || 0).toLocaleString()}</b></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Snapshot backup — write a mongodump archive to disk on demand */}
      <div className="glass-card p-4 border-l-4 border-emerald-500" data-testid="backup-card">
        <div className="flex items-start gap-3">
          <RefreshCw className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-slate-900">Snapshot backup</div>
            <div className="text-sm text-slate-600 mt-1">
              Runs <code>mongodump</code> and writes a gzipped archive to <code>/app/backups</code> inside
              the backend container. Take one <b>before every risky operation</b> (wipe, purge, mass edit).
              For durable off-box backups, deploy the sidecar service in
              <code> docker-compose.backup.yml</code> — nightly snapshots + S3 upload + 14-day retention.
            </div>
            <div className="mt-3">
              <Button
                onClick={runBackup}
                disabled={backupBusy}
                data-testid="backup-now-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {backupBusy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Backing up…</> : "Take snapshot now"}
              </Button>
            </div>
            {backupError && (
              <div className="mt-3 text-sm text-red-600" data-testid="backup-error">{backupError}</div>
            )}
            {backupResult && (
              <div className="mt-3 glass-solid p-3 text-sm text-emerald-800 bg-emerald-50/60" data-testid="backup-result">
                <div className="font-medium mb-1">✓ Snapshot written</div>
                <div className="text-xs font-mono break-all">{backupResult.archive}</div>
                <div className="text-xs text-slate-600 mt-1">
                  {backupResult.size_mb} MB · {new Date(backupResult.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ☢ Nuclear wipe — reset every analytics/upload collection to zero */}
      <div className="glass-card p-4 border-l-4 border-red-700 bg-red-50/30" data-testid="wipe-all-card">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-900 uppercase tracking-wide text-sm">
              ☢ Nuclear wipe — start fresh
            </div>
            <div className="text-sm text-slate-700 mt-1">
              Deletes EVERY <code>ops_call_row</code>, upload, <code>report_row</code>, report,
              and rollup doc. Preserves users, teams, brands, campaigns, and DB connections.
              Use this when you want to re-upload from scratch. <b>Cannot be undone.</b>
            </div>
            <div className="mt-3">
              <Button
                onClick={wipeAll}
                disabled={wipeBusy}
                data-testid="wipe-all-btn"
                className="bg-red-700 hover:bg-red-800 text-white"
              >
                {wipeBusy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Wiping…</> : "Wipe ALL analytics data"}
              </Button>
            </div>
            {wipeError && (
              <div className="mt-3 text-sm text-red-600" data-testid="wipe-error">{wipeError}</div>
            )}
            {wipeResult && (
              <div className="mt-3 glass-solid p-3 text-sm text-emerald-800 bg-emerald-50/60" data-testid="wipe-result">
                <div className="font-medium mb-1">✓ Wipe complete — you can now upload fresh files.</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                  {Object.entries(wipeResult.deleted || {}).map(([k, v]) => (
                    <div key={k}><span className="text-slate-500">{k}:</span> <b>{Number(v).toLocaleString()}</b></div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  Preserved: {(wipeResult.preserved || []).join(", ")}.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="glass-card p-4 border-l-4 border-red-500 flex gap-2 items-start">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-red-700">Diagnostic failed</div>
            <div className="text-sm text-slate-600">{error}</div>
          </div>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total rows"                value={data.total_rows} />
            <Stat label="Missing attempt_date_ist"  value={data.rows_with_null_attempt_date_ist + data.rows_with_empty_attempt_date_ist}
                  hint={(data.rows_with_null_attempt_date_ist + data.rows_with_empty_attempt_date_ist) > 0
                        ? "⚠ These rows are invisible to the date filter — run Retag to fix"
                        : "OK — all rows have a date"} />
            <Stat label="Rows with null"    value={data.rows_with_null_attempt_date_ist} />
            <Stat label="Rows with empty ''" value={data.rows_with_empty_attempt_date_ist} />
          </div>

          <div className="glass-card p-4">
            <div className="font-outfit font-semibold text-slate-900 mb-3">
              Recent distinct dates (last 15) — with row counts
            </div>
            {(data.recent_distinct_dates || []).length === 0 ? (
              <div className="text-sm text-slate-500">No date buckets found in the data.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 text-xs uppercase">
                    <th className="text-left py-2 px-2">attempt_date_ist</th>
                    <th className="text-right py-2 px-2">Row count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_distinct_dates.map((d) => (
                    <tr key={d} className="border-t border-white/60">
                      <td className="py-1.5 px-2 font-mono">{d}</td>
                      <td className="py-1.5 px-2 text-right">{(data.per_date_counts?.[d] || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="glass-card p-4 overflow-x-auto">
            <div className="font-outfit font-semibold text-slate-900 mb-3">
              Sample rows (10 most recent by attempt_datetime)
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 text-[10px] uppercase">
                  <th className="text-left py-1 px-2">Customer</th>
                  <th className="text-left py-1 px-2">Brand</th>
                  <th className="text-left py-1 px-2">Campaign</th>
                  <th className="text-left py-1 px-2">attempt_datetime</th>
                  <th className="text-left py-1 px-2">attempt_date_ist</th>
                  <th className="text-left py-1 px-2">attempt_date (legacy)</th>
                </tr>
              </thead>
              <tbody>
                {(data.sample_rows || []).map((r, i) => (
                  <tr key={i} className="border-t border-white/60">
                    <td className="py-1 px-2">{r.customer_name || "—"}</td>
                    <td className="py-1 px-2">{r.brand_id ?? "—"}</td>
                    <td className="py-1 px-2">{r.campaign_name || "—"}</td>
                    <td className="py-1 px-2 font-mono">{r.attempt_datetime || "—"}</td>
                    <td className="py-1 px-2 font-mono">
                      {r.attempt_date_ist || <span className="text-red-600 font-semibold">null</span>}
                    </td>
                    <td className="py-1 px-2 font-mono text-slate-500">{r.attempt_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="glass-card p-3">
            <summary className="cursor-pointer text-sm text-slate-600">Raw JSON</summary>
            <pre className="text-[11px] mt-2 max-h-96 overflow-auto bg-slate-50 p-3 rounded">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {!data && !loading && !error && (
        <div className="glass-card p-8 text-center text-slate-500">
          Click &quot;Run diagnostic&quot; to inspect date coverage on your ops data.
        </div>
      )}
      </>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="glass-card p-3">
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold text-slate-900 mt-1">
        {(value ?? 0).toLocaleString()}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function Detail({ label, value }) {
  const v = (value === null || value === undefined || value === "") ? "—" : String(value);
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
      <div className="font-mono text-slate-700 truncate" title={v}>{v}</div>
    </div>
  );
}

function CallCard({ c, i }) {
  // Build a plain-english timeline so ops can read the credit decision at a glance.
  const buildStory = () => {
    const parts = [];
    parts.push(`On ${c.attempt_datetime || c.attempt_date_ist || "?"}, agent ${c.agent_name || c.agent_id || "?"} called this customer.`);
    parts.push(`Disposition was "${c.disposition_name || "—"}".`);
    if (c.last_deposit_date_before) {
      parts.push(`Customer's previous deposit was on ${c.last_deposit_date_before} (${c.days_since_last_deposit_before ?? "?"} days before the call).`);
    } else {
      parts.push("Customer had never deposited before this call.");
    }
    if (c.first_deposit_on_day_date) {
      parts.push(`They deposited on ${c.first_deposit_on_day_date} (same day as the call).`);
    } else if (c.last_deposit_date_after) {
      parts.push(`They deposited on ${c.last_deposit_date_after} (after the call).`);
    }
    if (c.applied_rule) {
      parts.push(`Campaign rule → cooldown=${c.applied_rule.min_inactive_days}d, window=${c.applied_rule.allowed_period_after_call}d.`);
    }
    if (c.credit_status === "yes") {
      parts.push("→ Agent gets FULL credit for this conversion.");
    } else if (c.credit_status === "sms") {
      parts.push("→ SMS/voicemail credit — didn't reach live, but message helped convert.");
    } else if (c.credit_status === "unsure") {
      parts.push("→ Unsure credit — disposition suggests wrong/invalid number but a deposit followed.");
    } else {
      parts.push(`→ No credit. Reason: ${c.credit_reason}`);
    }
    return parts.join(" ");
  };

  const statusBadge =
    c.credit_status === "yes"
      ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> credit=yes</span>
      : c.credit_status === "sms"
      ? <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold inline-flex items-center gap-1">📱 credit=sms</span>
      : c.credit_status === "unsure"
      ? <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold inline-flex items-center gap-1">? credit=unsure</span>
      : <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> credit=no</span>;

  return (
    <div className="glass-card p-3" data-testid={`user-call-${i}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm font-mono text-slate-700">
          <span className="font-semibold text-slate-900">{c.customer_name || "—"}</span>{" · "}
          {c.attempt_datetime || "—"} <span className="text-xs text-slate-400">({c.attempt_date_ist || "?"})</span>
          {c.source && c.source !== "upload" && (
            <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 font-sans" title={`ingested via ${c.source}`}>
              {c.source}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {c.laaffic_voice_url && (
            <a href={c.laaffic_voice_url} target="_blank" rel="noreferrer"
               title={c.duration_seconds ? `Play recording · ${c.duration_seconds}s` : "Play recording"}
               className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
               data-testid={`call-recording-${i}`}>
              <PlayCircle className="w-3.5 h-3.5" />
              Recording
            </a>
          )}
          {statusBadge}
        </div>
      </div>

      {/* Plain-english narrative timeline */}
      <div className="text-sm text-slate-800 mb-2 leading-relaxed bg-slate-50/60 rounded p-2">
        {buildStory()}
      </div>

      {/* Timeline chips: call date · deposit date · credit date */}
      <div className="flex flex-wrap gap-2 text-[11px] mb-2">
        <span className="px-2 py-0.5 bg-slate-100 rounded-full">
          📞 Call: <span className="font-semibold">{c.attempt_date_ist || "?"}</span>
        </span>
        {(c.first_deposit_on_day_date || c.last_deposit_date_after) && (
          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded-full">
            💰 Deposit: <span className="font-semibold">
              {c.first_deposit_on_day_date || c.last_deposit_date_after}
            </span>
          </span>
        )}
        {c.credit_status !== "no" && (
          <span className="px-2 py-0.5 bg-blue-50 text-blue-800 rounded-full">
            ✓ Credit awarded on call date: <span className="font-semibold">{c.attempt_date_ist || "?"}</span>
          </span>
        )}
      </div>

      <div className="text-sm text-slate-700 mb-2 italic">
        Reason: &quot;{c.credit_reason || "—"}&quot;
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Detail label="Brand" value={c.brand_id} />
        <Detail label="Campaign" value={c.campaign_name} />
        <Detail label="Agent" value={c.agent_name || c.agent_id} />
        <Detail label="Disposition" value={c.disposition_name} />
        <Detail label="Fresh / Follow-up" value={c.is_fresh ? "Fresh" : c.is_followup ? "Follow-up" : "—"} />
        <Detail label="Reactivated?" value={c.reactivated_flag ? "yes" : "no"} />
        <Detail label="Inactive days (file)" value={c.inactive_days_snapshot} />
        <Detail label="Days since last deposit" value={c.days_since_last_deposit_before} />
        <Detail label="Last deposit before call" value={c.last_deposit_date_before} />
        <Detail label="Deposits before" value={c.deposit_count_before} />
        <Detail label="Deposits on-day" value={c.deposit_on_day_count} />
        <Detail label="Deposits after" value={c.deposit_count_after} />
        <Detail label="First deposit on-day" value={c.first_deposit_on_day_date} />
        <Detail label="Last deposit after" value={c.last_deposit_date_after} />
        <Detail label="Registration" value={c.registration_date} />
        <Detail label="Upload" value={c.upload_id ? `${c.upload_id.slice(0, 8)}…` : "—"} />
      </div>
      {c.applied_rule && (
        <div className="mt-2 text-xs text-slate-500 border-t border-slate-100 pt-2">
          Rule applied → <span className="font-mono text-slate-700">min_inactive_days={c.applied_rule.min_inactive_days}</span>
          {" · "}<span className="font-mono text-slate-700">allowed_period_after_call={c.applied_rule.allowed_period_after_call}d</span>
        </div>
      )}
    </div>
  );
}
